import React, { useEffect } from 'react';
import { Hidden } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useHistory, useLocation } from 'react-router-dom';
import { SearchApp } from '../../../../search/components/search-app';
import { SearchProps } from '../../models/arc-epi-props';
import { useInsuranceGroups } from './useInsuranceGroups';
import { GroupListLabels } from './group-list-labels';
import { SearchList } from '../../../../search/components/search-list';
import { GroupItemView } from './group-item-view';
import { ListPagination } from '../../../../search/components/list-pagination';
import { FilterGroup } from '../../../../search/components/filter-group';
import { SearchBar } from '../../../../search/components/search-bar';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ArcRoutes } from '../../constants/routes';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { Group, GroupsState } from './groups-model';
import { setCurrentGroup } from './groups-slice';
import { sortTableStyles } from '../../../../global/constants/theme/styles/tables/generic-sort-table';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { unloadBillingAccounts } from '../billing-accounts/billing-accounts-slice';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { setNavigationContext } from '../../../../global/ducks/app-context';
import { NavigationContext } from '../../../../global/models/app-context-model';
import { useGroupSearchReset } from '../../hooks/useGroupsReset';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';

export function GroupSearch() {
    const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
    const groupState = useSelector<TcsmStates, GroupsState>(state => state.groupsState);
    const billingAccountSearch = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}`);
    const groupsLoaded = useInsuranceGroups();
    const tableClasses = sortTableStyles();
    const dispatch = useDispatch();
    const userNavigationContext = useSelector<TcsmStates, NavigationContext>(state => state.appContext.userNavigationContext);
    const { state } = useLocation<any>();
    const history = useHistory();
    const gpsItem = getGpsType(GpsType.GROUP_LIST, null);

    useGroupSearchReset(state?.ignoreEmploymentReset);

    useEffect(() => {
        dispatch(unloadBillingAccounts());
        if (groupState.groups?.length > 1) {
            dispatch(setCurrentGroup(null));
        }
    }, []);

    useEffect(() => {
		if (userNavigationContext === NavigationContext.GroupAdmin){
			dispatch(setNavigationContext(NavigationContext.None));
		}		
	}, [userNavigationContext]);

	if (!groupsLoaded) {
		return <LoadingCircle />;
	}

    const onSet = (group: Group, forceCurrentGroupSet: boolean) => {
        if (forceCurrentGroupSet) {
            dispatch(setCurrentGroup(group));
            dispatch(setNavigationContext(NavigationContext.GroupAdmin));
        }

        history.push(state?.actionLink || billingAccountSearch, state);
    };

    // bypass group selection if only has one group
    if (groupState.groups?.length === 1) {
        // Keep the function pure.
        setTimeout(
            () => {            
            onSet(groupState.currentGroup || groupState.groups[0], false);
        }, 10);

        return <LoadingCircle />;
    }

    return (
        <>
            <PageHeader
                headerText={searchSettings.groupSearchPageTitle}
            />
            <Hidden mdUp>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
            
            <SearchApp initialList={groupState?.groups} appName='GroupSelection'>
                <FilterGroup hide={groupState?.groups?.length < 10}>
                    <SearchBar label={searchSettings.groupSearchLabel} name='group-search-bar' filterProps={['group_id', 'name']} />
                </FilterGroup>
                <div className={tableClasses.sortTable}>
                    <Hidden xsDown>
                        <GroupListLabels settings={searchSettings} />
                        </Hidden>
                <div className='sort-table-content'>
                    <SearchList
                        renderList={
                            (group: Group) => {
                                return <GroupItemView settings={searchSettings} key={group.group_id} group={group} onSelect={(selectedGroup: Group) => onSet(selectedGroup, true)} />;
                            }
                        }
                        renderPagination={
                            (totalCounts, name) => {
                                return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 10)} nextButton={searchSettings.nextButton} prevButton={searchSettings.previousButton} />;
                            }
                        }
                    />
                    </div>
                </div>
            </SearchApp>
        </>
    );
}